import { z } from 'zod'

export const AssertSchema = z
  .object({
    role: z.string(),
    name: z.string().optional(),
    testId: z.string().optional(),
    text: z.string().optional(),
    selector: z.string().optional(),
    url: z.string().optional(),
    visible: z.boolean().optional(),
    count: z.number().int().nonnegative().optional(),
    probabilistic: z
      .object({
        confidence: z.number().min(0).max(1),
        fallback: z.enum(['warn', 'fail']).optional(),
      })
      .optional(),
  })
  .strict()

const WaitForSchema = z
  .object({
    kind: z.enum(['selector', 'url', 'networkidle']),
    value: z.string().optional(),
    timeout: z.number().int().positive().optional(),
  })
  .strict()

const SecretRefSchema = z.object({ secret: z.string().min(1) }).strict()
const FixtureRefSchema = z.object({ fixture: z.string().min(1) }).strict()
const InputValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  SecretRefSchema,
  FixtureRefSchema,
])

const TransitionSchema = z
  .object({
    on: z.string().min(1),
    to: z.string().min(1),
    guard: z.string().min(1).optional(),
    waitFor: WaitForSchema.optional(),
  })
  .strict()

/** @type {[string, ...string[]]} */
const HOOK_VOCABULARY = [
  'clear_cookies',
  'clear_local_storage',
  'logout',
  'seed_user',
  'reset_db_via_endpoint',
]

const NodeSchema = z
  .object({
    route: z.string(),
    subState: z.string().optional(),
    asserts: z.array(AssertSchema).optional(),
    inputs: z.record(InputValueSchema).optional(),
    transitions: z.array(TransitionSchema).optional(),
    fingerprint: z
      .object({
        accessibleName: z.string().optional(),
        testId: z.string().optional(),
        visibleText: z.string().optional(),
        selectorChain: z.array(z.string()).optional(),
        parentRoute: z.string().optional(),
        parentRegion: z.string().optional(),
      })
      .optional(),
    subflow: z
      .object({
        name: z.string().min(1),
        version: z.string().optional(),
        overrides: z.record(z.any()).optional(),
      })
      .optional(),
    framework: z.record(z.record(z.any())).optional(),
  })
  .strict()

const EnvSchema = z
  .object({
    url: z.string().url(),
  })
  .strict()

const HooksSchema = z
  .object({
    pre: z.array(z.enum(HOOK_VOCABULARY)).optional(),
    post: z.array(z.enum(HOOK_VOCABULARY)).optional(),
  })
  .strict()

export const FlowSchema = z
  .object({
    apiVersion: z.literal('cairntools.com/v1alpha1'),
    kind: z.literal('Flow'),
    metadata: z
      .object({
        name: z.string().min(1),
        description: z.string().optional(),
        owner: z.string().optional(),
        tags: z.array(z.string()).optional(),
        platform: z.enum(['web', 'mobile']).optional(),
      })
      .strict(),
    spec: z
      .object({
        entry: z.string().min(1),
        envs: z.record(EnvSchema),
        hooks: HooksSchema.optional(),
        nodes: z.record(NodeSchema),
      })
      .strict(),
  })
  .strict()

export { HOOK_VOCABULARY }
